import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'

import { CommonPaths } from '@utils/path'

import {
  MasterTransaction,
  isTransactionFormRedirect,
  isTransactionHistory,
  isTransactionSuccessful,
  isTransactionUnSuccessful,
  isTransactionUrlRedirect,
} from '@model/MasterTransaction'
import { isPaymentProviderBankType } from '@model/PaymentProviderType'
import type { WalletDepositFormValues } from '@model/WalletDepositFormValues'

import { safeLocalStorage } from '@hooks/useStorage'

interface UseDepositSubmitValue {
  handleDepositSubmit: (data: MasterTransaction, values: WalletDepositFormValues) => void
}

export const useDepositSubmit = (): UseDepositSubmitValue => {
  const { account } = useAccountReadContext()
  const navigate = useNavigate()
  const location = useLocation()
  const isSignupFlow = useMemo(() => location.pathname.includes('sign-up'), [location.pathname])
  const redirectPath = useMemo(() => {
    if (isSignupFlow) {
      const hasAdditionalId = safeLocalStorage.getItem('hasAdditionalId')
      return `/sign-up/${hasAdditionalId ? 'confirm' : `documents-info/${account?.id}`}`
    }

    return '/dashboard/traders-room/transaction-history'
  }, [isSignupFlow, account?.id])

  const handleDepositSubmit = (data: MasterTransaction, values: WalletDepositFormValues) => {
    if (isTransactionHistory(data)) {
      navigate(`/dashboard/traders-room/transaction-history`)
    }

    if (isPaymentProviderBankType(values.paymentProvider.providerCategory.id)) {
      navigate(
        `${CommonPaths.TradersRoomWallets}/${values.wallet.id}/deposit/transaction/succeed`,
        {
          state: {
            data,
            providerCategoryId: values.paymentProvider.providerCategory.id,
          },
        }
      )
    }

    if (isTransactionUrlRedirect(data)) {
      navigate(`${CommonPaths.TradersRoomWallets}/${values.wallet.id}/deposit/redirect`, {
        state: {
          transaction: data,
          values,
          redirectPath,
        },
      })
    }

    if (isTransactionFormRedirect(data)) {
      navigate(`${CommonPaths.TradersRoomWallets}/${values.wallet.id}/deposit/redirect`, {
        state: {
          transaction: data,
          values,
          redirectPath,
        },
      })
    }

    if (isTransactionSuccessful(data)) {
      navigate(
        `${CommonPaths.TradersRoomWallets}/${values.wallet.id}/deposit/transaction/succeed`,
        {
          state: {
            data,
            providerCategoryId: values.paymentProvider.providerCategory.id,
          },
        }
      )
    }

    if (isTransactionUnSuccessful(data)) {
      navigate(`${CommonPaths.TradersRoomWallets}/${values.wallet.id}/deposit/transaction/failed`, {
        state: {
          data,
          providerCategoryId: values.paymentProvider.providerCategory.id,
        },
      })
    }
  }

  return {
    handleDepositSubmit,
  }
}
